// Contenido de un kit resuelto contra el catálogo: ficha de cada componente y el ahorro. Puro.
import { itemDisplayName } from '@/shared/lib/format/itemDisplayName';
import { productTierLabel } from '@/shared/lib/format/productTier';
import { categoryOf } from './cartLines';
import type { CatalogItem, KitComponent, ProductCategory, ReservedLine } from './types';
import { variantCapacityLabel } from '@/shared/lib/format/capacity';

/** Lo que el modal necesita del kit; sirve igual para un item del catálogo o una línea apartada. */
export type KitSource = Pick<
    CatalogItem,
    'sku' | 'brand' | 'model' | 'description' | 'cashPriceCents'
> & {
    /** `undefined` cuando no se sabe la existencia (kit ya apartado fuera del catálogo). */
    stock?: number | null;
    components?: (KitComponent & { category?: ProductCategory })[];
};

export interface KitComponentDetail {
    sku: string;
    qty: number;
    category: ProductCategory | undefined;
    /** `false` si el SKU no está en el catálogo: solo se conoce el SKU. */
    resolved: boolean;
    /** Marca y modelo; si no hay, la descripción; si no, el SKU. */
    name: string;
    /** Descripción del catálogo cuando no es ya el nombre; `''` si no hay. */
    description: string;
    /** Capacidad, color, gama y categoría que traiga el catálogo, sin huecos. */
    facts: string[];
    unitPriceCents: number | null;
    stock: number | null;
}

export interface KitContents {
    components: KitComponentDetail[];
    /** Suma de listas × piezas; `null` si algún componente no tiene precio. */
    separateTotalCents: number | null;
    kitPriceCents: number;
    /** Precio con promo cuando baja el del kit; `null` sin promo. */
    promoPriceCents: number | null;
    /** Lo que se ahorra contra comprar por separado; `null` si no aplica. */
    savingsCents: number | null;
}

/** Categoría del componente en palabras de la tienda. */
export const COMPONENT_CATEGORY_LABEL: Record<ProductCategory, string> = {
    PHONE: 'Teléfono',
    ACCESSORY: 'Funda / accesorio',
    SIM: 'SIM',
    MODEM: 'Módem',
    BUNDLE: 'Kit',
};

/** Componentes del kit (filas del modal), contados para «Ver qué incluye (N)». */
export function kitComponentCount(kit: Pick<KitSource, 'components'>): number {
    return kit.components?.length ?? 0;
}

function detailOf(
    component: KitComponent & { category?: ProductCategory },
    item: CatalogItem | undefined,
): KitComponentDetail {
    const category = component.category ?? (item ? categoryOf(item) : undefined);
    const categoryLabel = category ? [COMPONENT_CATEGORY_LABEL[category]] : [];
    if (item === undefined) {
        return {
            sku: component.sku,
            qty: component.qty,
            category,
            resolved: false,
            name: component.sku,
            description: '',
            facts: categoryLabel,
            unitPriceCents: null,
            stock: null,
        };
    }
    const name = itemDisplayName(item);
    const description = item.description?.trim() ?? '';
    const tier = item.tier.trim();
    return {
        sku: component.sku,
        qty: component.qty,
        category,
        resolved: true,
        name,
        description: description !== name ? description : '',
        facts: [
            variantCapacityLabel(item),
            item.color.trim(),
            tier !== '' ? productTierLabel(tier) : '',
            ...categoryLabel,
        ].filter((part) => part !== ''),
        unitPriceCents: item.cashPriceCents,
        stock: item.stock,
    };
}

/** Resuelve cada componente con el catálogo, en orden alfabético, y calcula el ahorro del kit. */
export function kitContentsOf(
    kit: KitSource,
    items: readonly CatalogItem[],
    discountedPriceCents: number | null = null,
): KitContents {
    const bySku = new Map(items.map((i) => [i.sku, i]));
    const components = (kit.components ?? [])
        .map((c) => detailOf(c, bySku.get(c.sku)))
        .sort((a, b) => a.name.localeCompare(b.name, 'es-MX'));
    const separateTotalCents = components.every((c) => c.unitPriceCents !== null)
        ? components.reduce((sum, c) => sum + (c.unitPriceCents ?? 0) * c.qty, 0)
        : null;
    const promoPriceCents =
        discountedPriceCents !== null && discountedPriceCents < kit.cashPriceCents
            ? discountedPriceCents
            : null;
    const payCents = promoPriceCents ?? kit.cashPriceCents;
    const savingsCents =
        separateTotalCents !== null && components.length > 0 && separateTotalCents > payCents
            ? separateTotalCents - payCents
            : null;
    return {
        components,
        separateTotalCents: components.length > 0 ? separateTotalCents : null,
        kitPriceCents: kit.cashPriceCents,
        promoPriceCents,
        savingsCents,
    };
}

/** Kit del catálogo por SKU; si ya no está, el de la línea apartada con lo que congeló. */
export function kitSourceOf(
    sku: string,
    items: readonly CatalogItem[],
    anchor: ReservedLine | undefined,
): KitSource | null {
    const item = items.find((i) => i.sku === sku && (i.components?.length ?? 0) > 0);
    if (item !== undefined) return item;
    if (anchor === undefined || anchor.sku !== sku || anchor.components.length === 0) return null;
    return {
        sku,
        brand: anchor.product?.brand ?? '',
        model: anchor.product?.model ?? '',
        cashPriceCents: anchor.basePriceCents ?? 0,
        components: anchor.components,
    };
}
